// src/features/analytics/components/AnalyticsContentPage.tsx

import { useState } from 'react';
import { Badge, Group, Paper, SegmentedControl, Select, Skeleton, Stack, Table } from '@mantine/core';
import {
  ANALYTICS_METRICS,
  ANALYTICS_METRIC_LABELS,
  TOP_CONTENT_DEFAULT_LIMIT,
  TOP_CONTENT_MAX_LIMIT,
  type AnalyticsMetric,
} from '@/lib/constants/analytics';
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags';
import { AnalyticsApi } from '../AnalyticsApi';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { useAnalyticsRange } from '../useAnalyticsRange';
import { useAnalyticsScope } from '../useAnalyticsScope';
import { useAnalyticsTopContent } from '../useAnalyticsTopContent';
import { CsvDownloadButton } from '@/shared/components/CsvDownloadButton';
import type { TopContentRow } from '../types';
import { AnalyticsCoverageNotice } from './AnalyticsCoverageNotice';
import { AnalyticsErrorState } from './AnalyticsErrorState';
import { MediaAudienceDrawer } from './MediaAudienceDrawer';

const LIMIT_OPTIONS = ['20', '50', String(TOP_CONTENT_MAX_LIMIT)];

export function AnalyticsContentPage() {
  const { search, range } = useAnalyticsRange();
  const { scope } = useAnalyticsScope(search.scope);
  const [metric, setMetric] = useState<AnalyticsMetric>('watchTime');
  const [limit, setLimit] = useState(TOP_CONTENT_DEFAULT_LIMIT);
  // The clicked row drives the geo drill-down Drawer. Its cid is the account
  // owning the media, so the account-scoped media-audience route serves the
  // network tab's child rows too (the gateway authorizes parent→child reads).
  const [selectedRow, setSelectedRow] = useState<TopContentRow | null>(null);

  const { topContent, isLoading, isError, error, refetch } = useAnalyticsTopContent(
    scope,
    range,
    metric,
    limit
  );

  const rows = topContent?.data.rows ?? [];
  // Network scope spans sub-accounts: the api attributes each row with its
  // owning account's label (ADR-0012) and the table shows an Account column,
  // matching the Sub-Account Streams view pattern.
  const showAccount = search.scope === 'network';
  // Peak CCV decoration (metrics expansion) — the value arrives bundled in the
  // top-content rows either way; only rendering is gated.
  const showPeak = FEATURE_FLAGS.analyticsExpansionEnabled;

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Group gap="sm">
          <SegmentedControl
            size="xs"
            data={ANALYTICS_METRICS.map((m) => ({ value: m, label: ANALYTICS_METRIC_LABELS[m] }))}
            value={metric}
            onChange={(value) => setMetric(value as AnalyticsMetric)}
          />
          <Select
            size="xs"
            w={90}
            data={LIMIT_OPTIONS}
            value={String(limit)}
            onChange={(value) => value && setLimit(Number(value))}
            allowDeselect={false}
            aria-label="Row limit"
          />
        </Group>
        {scope && (
          <CsvDownloadButton
            download={() => AnalyticsApi.downloadTopContentCsv(scope, range, metric, limit)}
            fallbackFilename={`analytics-${scope.kind}-top-content.csv`}
          />
        )}
      </Group>

      {isError ? (
        <AnalyticsErrorState error={error} onRetry={refetch} />
      ) : isLoading ? (
        <Skeleton height={240} />
      ) : rows.length === 0 ? (
        <AnalyticsCoverageNotice
          coverage={topContent?.coverage}
          freshness={topContent?.freshness}
          emptyMessage="No playback analytics for this range. Playback analytics begins with the new player rollout."
        />
      ) : (
        <Paper withBorder>
          <AnalyticsCoverageNotice
            coverage={topContent?.coverage}
            freshness={topContent?.freshness}
          />
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={50}>#</Table.Th>
                <Table.Th>Title</Table.Th>
                <Table.Th w={70}>Type</Table.Th>
                {showAccount && <Table.Th>Account</Table.Th>}
                <Table.Th style={{ textAlign: 'right' }}>Sessions</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Watch time</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Unique viewers</Table.Th>
                {showPeak && <Table.Th style={{ textAlign: 'right' }}>Peak CCV</Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row, index) => (
                <Table.Tr
                  key={`${row.cid}-${row.mediaId}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedRow(row)}
                >
                  <Table.Td>{index + 1}</Table.Td>
                  {/* Title decoration fallback is the raw mediaId STRING (support correlation id) */}
                  <Table.Td>{row.title ?? row.mediaId}</Table.Td>
                  {/* streamType is best-effort decoration — cell stays empty when the lookup missed */}
                  <Table.Td>
                    {row.streamType && (
                      <Badge size="sm" variant="light" color={row.streamType === 'live' ? 'blue' : 'gray'}>
                        {row.streamType === 'live' ? 'Live' : 'VOD'}
                      </Badge>
                    )}
                  </Table.Td>
                  {showAccount && <Table.Td>{row.label ?? String(row.cid)}</Table.Td>}
                  <Table.Td style={{ textAlign: 'right' }}>
                    {AnalyticsUtils.formatCount(row.sessions)}
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {AnalyticsUtils.formatWatchTime(row.watchTimeMs)}
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {AnalyticsUtils.formatCount(row.uniqueViewers)}
                  </Table.Td>
                  {showPeak && (
                    <Table.Td style={{ textAlign: 'right' }}>
                      {/* Em dash = unmeasured (absent), never a fabricated zero. */}
                      {row.peakConcurrent !== undefined
                        ? AnalyticsUtils.formatCount(row.peakConcurrent)
                        : '—'}
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <MediaAudienceDrawer
        cid={selectedRow?.cid ?? null}
        row={selectedRow}
        range={range}
        onClose={() => setSelectedRow(null)}
      />
    </Stack>
  );
}
